import { loadFS } from './fs';

// Inlined to avoid `node:path`, which Vite shims to an empty module in the
// browser bundles that transitively import this file.
const posixDirname = (path: string): string => {
  const slashIdx = path.lastIndexOf('/');

  if (slashIdx < 0) return '.';
  if (slashIdx === 0) return '/';

  return path.substring(0, slashIdx);
};

let tmpCounter = 0;

const atomicWriteFile = async (path: string, data: string) => {
  const fs = await loadFS();
  tmpCounter += 1;
  const tmpPath = `${path}.${process.pid}.${Date.now().toString(36)}.${tmpCounter.toString(36)}.tmp`;

  let tmpHandle: Awaited<ReturnType<typeof fs.open>> | undefined;
  let renamed = false;

  try {
    // Without the tmp-file fsync and parent-dir fsync, a power loss between
    // rename and data persistence can leave a zero-byte target file.
    tmpHandle = await fs.open(tmpPath, 'w');
    await tmpHandle.writeFile(data);
    await tmpHandle.sync();
    await tmpHandle.close();
    tmpHandle = undefined;

    await fs.rename(tmpPath, path);
    renamed = true;

    const dirHandle = await fs.open(posixDirname(path), 'r');

    try {
      await dirHandle.sync();
    } finally {
      await dirHandle.close();
    }
  } catch (e) {
    if (tmpHandle) {
      try {
        await tmpHandle.close();
      } catch {
        /* */
      }
    }

    // After a successful rename, tmpPath no longer exists and the target
    // file should be left in place even if the parent-dir fsync threw.
    if (!renamed) {
      try {
        await fs.unlink(tmpPath);
      } catch {
        /* */
      }
    }

    throw e;
  }
};

export const readJSONFile = async <T>(path: string): Promise<T | void> => {
  const fs = await loadFS();

  try {
    return JSON.parse(await fs.readFile(path, 'utf-8')) as T;
  } catch (e) {
    return undefined;
  }
};

export const readJSONFileWithFallback = async <T>(
  path: string,
  fallback: T,
): Promise<T> => {
  try {
    const fs = await loadFS();

    return JSON.parse(await fs.readFile(path, 'utf-8')) as T;
  } catch (e) {
    return fallback;
  }
};

export const writeJsonFile = async <T>(path: string, data: T) => {
  await atomicWriteFile(path, JSON.stringify(data));
};

export const moveFile = async (source: string, destination: string) => {
  const fs = await loadFS();

  await fs.rename(source, destination);

  return destination;
};

export const writeJSONFileWithDefaults = async <T>(
  path: string,
  data: Partial<T>,
  fallback: T,
) => {
  const file = await readJSONFileWithFallback<T>(path, fallback);

  if (!file) {
    throw new Error(`Could not load file at ${path}`);
  }

  const newFile = {
    ...file,
    ...data,
  };

  await atomicWriteFile(path, JSON.stringify(newFile));

  return newFile;
};

export const writePlainTextFile = async (path: string, data: string) => {
  await atomicWriteFile(path, data);
};

/**
 * Create an in-process write queue: every operation passed to the returned
 * function runs only after the previous one has settled. Use one queue per
 * file whose writes are read-merge-writes of the whole file — two overlapping
 * writes could otherwise interleave so the later write resurrects the value
 * the earlier one just replaced. Only sufficient when this process is the
 * file's sole writer.
 */
export const makeWriteQueue = () => {
  let pendingWrite: Promise<unknown> = Promise.resolve();

  return <T>(operation: () => Promise<T>): Promise<T> => {
    // Run after the previous write regardless of whether it succeeded; a
    // failed write must not wedge the queue.
    const next = pendingWrite.then(operation, operation);

    // Swallow this operation's rejection on the queue itself (the caller
    // still gets it via `next`), so a failure doesn't reject every later
    // write too.
    pendingWrite = next.catch(() => undefined);

    return next;
  };
};
